import type { Item } from "@artis/data-grid";
import { useCallback, useRef, useState } from "react";

import {
  cellKey,
  sheetNamesEqual,
  usedExtent,
  renameSheetRefs,
  nextSheetNumber,
  type PersistedWorkbook,
  type Cell,
  type Sheet,
} from "@package/scratchpad-utils";
import { createCurveCache } from "../formula/curves";
import { FUNCTIONS } from "../formula/functions";
import { createGraph, type RefKey } from "../formula/graph";
import { createHistory } from "../formula/history";
import { applyMoveRange } from "../formula/move";
import { normalizeFormula } from "../formula/normalize";
import { applyCellPatches, type CellPatch, diffSheetCells } from "../formula/patch";
import type { CellRect } from "../formula/picking";
import type { Graph } from "../types";

export function itemKey(cell: Item): string {
  return cellKey(cell[0], cell[1]);
}

type Structural =
  | { kind: "add"; sheetId: string; name: string; index: number }
  | { kind: "delete"; sheetId: string; name: string; index: number }
  | { kind: "rename"; sheetId: string; before: string; after: string };

type UndoEntry = { patches: CellPatch[]; structural?: Structural; activeSheetId: string };

const HISTORY_CAP = 100;

function emptySheet(id: string, name: string): Sheet {
  return { id, name, cells: {}, used: { rows: 0, cols: 0 } };
}

function insertAt(sheets: Sheet[], index: number, sheet: Sheet): Sheet[] {
  const i = Math.min(Math.max(index, 0), sheets.length);
  return [...sheets.slice(0, i), sheet, ...sheets.slice(i)];
}

function hydrate(base: Sheet[], g: Graph): Sheet[] {
  for (const s of base) {
    for (const [key, cell] of Object.entries(s.cells)) g.set(`${s.id}!${key}`, cell.src);
  }
  g.recompute();
  return base.map((s) => ({
    ...s,
    cells: Object.fromEntries(
      Object.entries(s.cells).map(([key, cell]) => {
        const node = g.get(`${s.id}!${key}`);
        return [key, { ...cell, value: node?.value ?? null, error: node?.error ?? null }];
      }),
    ),
  }));
}

export function useWorkbook(initial?: PersistedWorkbook | null) {
  const sheetsRef = useRef<Sheet[]>([]);

  const refKey = useCallback<RefKey>((ownerId, sheet, col, row) => {
    const ownerSheet = ownerId.slice(0, ownerId.indexOf("!"));
    if (sheet) {
      const found = sheetsRef.current.find((s) => sheetNamesEqual(s.name, sheet));
      if (!found) return null;
      return `${found.id}!${cellKey(col, row)}`;
    }
    return `${ownerSheet}!${cellKey(col, row)}`;
  }, []);

  const curvesRef = useRef(createCurveCache());
  const graphRef = useRef<Graph | null>(null);

  const [sheets, setSheets] = useState<Sheet[]>(() => {
    const base = initial?.sheets ?? [emptySheet("s1", "Sheet1")];
    sheetsRef.current = base;
    const g = createGraph(FUNCTIONS, refKey, curvesRef.current.resolve);
    graphRef.current = g;
    return hydrate(base, g);
  });
  const [activeSheetId, setActiveSheetId] = useState(initial?.activeSheetId ?? "s1");
  const nextSheetId = useRef(initial ? nextSheetNumber(initial.sheets) : 2);

  sheetsRef.current = sheets;
  const activeSheetIdRef = useRef(activeSheetId);
  activeSheetIdRef.current = activeSheetId;

  const historyRef = useRef(createHistory<UndoEntry>(HISTORY_CAP));
  const pendingRef = useRef<{ depth: number; patches: CellPatch[]; activeSheetId: string } | null>(null);

  const record = useCallback((patches: CellPatch[], structural?: Structural) => {
    if (patches.length === 0 && !structural) return;
    const pending = pendingRef.current;
    if (pending && pending.depth > 0 && !structural) {
      pending.patches.push(...patches);
      return;
    }
    historyRef.current.push({ patches, structural, activeSheetId: activeSheetIdRef.current });
  }, []);

  const transact = useCallback((fn: () => void) => {
    if (!pendingRef.current) {
      pendingRef.current = { depth: 0, patches: [], activeSheetId: activeSheetIdRef.current };
    }
    pendingRef.current.depth++;
    try {
      fn();
    } finally {
      pendingRef.current.depth--;
      if (pendingRef.current.depth === 0) {
        const { patches, activeSheetId: active } = pendingRef.current;
        pendingRef.current = null;
        if (patches.length > 0) historyRef.current.push({ patches, activeSheetId: active });
      }
    }
  }, []);

  const flushScheduledRef = useRef(false);
  const subscribedRef = useRef(false);
  if (!subscribedRef.current) {
    subscribedRef.current = true;
    const g = graphRef.current!;
    g.subscribeExternals((key, active) => {
      if (active) curvesRef.current.begin(key);
      else curvesRef.current.end(key);
    });
    curvesRef.current.subscribe((key) => {
      g.dirtyExternal(key);
      if (flushScheduledRef.current) return;
      flushScheduledRef.current = true;
      queueMicrotask(() => {
        flushScheduledRef.current = false;
        g.recompute();
        setSheets((prev) => prev.map((sheet) => ({ ...sheet })));
      });
    });
  }
  const graph = graphRef.current!;

  const nodeKey = useCallback((sheetId: string, cell: Item) => `${sheetId}!${itemKey(cell)}`, []);

  const activeSheet = sheets.find((s) => s.id === activeSheetId) ?? sheets[0]!;

  const getValue = useCallback((cell: Item) => activeSheet.cells[itemKey(cell)]?.src ?? "", [activeSheet]);

  const getNode = useCallback((cell: Item) => graph.get(nodeKey(activeSheetId, cell)), [graph, nodeKey, activeSheetId]);

  const setValue = useCallback(
    (cell: Item, input: string) => {
      const raw = normalizeFormula(input, (name) => sheetsRef.current.find((s) => sheetNamesEqual(s.name, name))?.name);
      const key = itemKey(cell);
      const before = sheetsRef.current.find((s) => s.id === activeSheetId)?.cells[key]?.src ?? null;
      const after = raw === "" ? null : raw;
      if (before !== after) record([{ sheetId: activeSheetId, key, before, after }]);
      const id = nodeKey(activeSheetId, cell);
      if (raw === "") {
        graph.remove(id);
      } else {
        graph.set(id, raw);
      }
      graph.recompute();
      const node = graph.get(id);
      setSheets((prev) =>
        prev.map((s) => {
          if (s.id !== activeSheetId) return s;
          const cells = { ...s.cells };
          let used = s.used;
          if (raw === "") {
            delete cells[itemKey(cell)];
            used = usedExtent(cells);
          } else {
            cells[itemKey(cell)] = { src: raw, value: node?.value ?? null, error: node?.error ?? null };
            const rows = Math.max(used.rows, cell[1] + 1);
            const cols = Math.max(used.cols, cell[0] + 1);
            if (rows !== used.rows || cols !== used.cols) used = { rows, cols };
          }
          return { ...s, cells, used };
        }),
      );
    },
    [activeSheetId, graph, nodeKey, record],
  );

  const moveRange = useCallback(
    (rect: CellRect, target: Item) => {
      const {
        sheets: next,
        removals,
        updates,
      } = applyMoveRange(sheets, activeSheetId, rect, target[0] - rect.x, target[1] - rect.y);
      if (next === sheets) return;
      record(diffSheetCells(sheets, next));
      sheetsRef.current = next;
      setSheets(next);
      for (const id of removals) graph.remove(id);
      for (const u of updates) graph.set(u.nodeId, u.src);
      graph.recompute();
    },
    [sheets, activeSheetId, graph, record],
  );

  const addSheet = useCallback(() => {
    const n = nextSheetId.current++;
    const id = `s${n}`;
    record([], { kind: "add", sheetId: id, name: `Sheet${n}`, index: sheetsRef.current.length });
    setSheets((prev) => [...prev, { id, name: `Sheet${n}`, cells: {}, used: { rows: 0, cols: 0 } }]);
    setActiveSheetId(id);
  }, [record]);

  const deleteSheet = useCallback(
    (id: string) => {
      if (sheets.length <= 1) return;
      const idx = sheets.findIndex((s) => s.id === id);
      const sheet = sheets[idx];
      if (!sheet) return;
      record(
        Object.entries(sheet.cells).map(([key, cell]) => ({ sheetId: id, key, before: cell.src, after: null })),
        { kind: "delete", sheetId: id, name: sheet.name, index: idx },
      );
      const next = sheets.filter((s) => s.id !== id);
      sheetsRef.current = next;
      setSheets(next);
      if (id === activeSheetId) setActiveSheetId(next[Math.max(0, idx - 1)]!.id);
      for (const key of Object.keys(sheet.cells)) graph.remove(`${id}!${key}`);
      graph.recompute();
    },
    [sheets, activeSheetId, graph, record],
  );

  const renameSheet = useCallback(
    (id: string, name: string) => {
      const oldName = sheets.find((s) => s.id === id)?.name;
      if (!oldName || oldName === name) return;
      const updates: Array<{ nodeId: string; src: string }> = [];
      const structural: Structural = { kind: "rename", sheetId: id, before: oldName, after: name };
      const next = sheets.map((s) => {
        const sheet = s.id === id ? { ...s, name } : s;
        const cells: Record<string, Cell> = {};
        let changed = s.id === id;
        for (const [key, cell] of Object.entries(sheet.cells)) {
          const src = renameSheetRefs(cell.src, oldName, name);
          if (src === cell.src) {
            cells[key] = cell;
          } else {
            cells[key] = { ...cell, src };
            updates.push({ nodeId: `${sheet.id}!${key}`, src });
            changed = true;
          }
        }
        return changed ? { ...sheet, cells } : s;
      });
      record(diffSheetCells(sheets, next), structural);
      sheetsRef.current = next;
      setSheets(next);
      for (const u of updates) graph.set(u.nodeId, u.src);
      if (updates.length) graph.recompute();
    },
    [sheets, graph, record],
  );

  const applyEntry = useCallback(
    (entry: UndoEntry, direction: "undo" | "redo") => {
      let current = sheetsRef.current;
      const s = entry.structural;
      if (s) {
        const removeSheet = (s.kind === "add" && direction === "undo") || (s.kind === "delete" && direction === "redo");
        if (s.kind === "rename") {
          const name = direction === "undo" ? s.before : s.after;
          current = current.map((sheet) => (sheet.id === s.sheetId ? { ...sheet, name } : sheet));
        } else if (removeSheet) {
          current = current.filter((sheet) => sheet.id !== s.sheetId);
          for (const p of entry.patches) graph.remove(`${p.sheetId}!${p.key}`);
        } else {
          current = insertAt(current, s.index, emptySheet(s.sheetId, s.name));
        }
      }
      const { sheets: next, removals, updates } = applyCellPatches(current, entry.patches, direction);
      sheetsRef.current = next;
      setSheets(next);
      for (const id of removals) graph.remove(id);
      for (const u of updates) graph.set(u.nodeId, u.src);
      graph.recompute();
      if (next.some((sheet) => sheet.id === entry.activeSheetId)) {
        setActiveSheetId(entry.activeSheetId);
      } else if (!next.some((sheet) => sheet.id === activeSheetIdRef.current)) {
        setActiveSheetId(next[0]!.id);
      }
    },
    [graph],
  );

  const undo = useCallback(() => {
    const entry = historyRef.current.undo();
    if (entry) applyEntry(entry, "undo");
  }, [applyEntry]);

  const redo = useCallback(() => {
    const entry = historyRef.current.redo();
    if (entry) applyEntry(entry, "redo");
  }, [applyEntry]);

  const setColWidth = useCallback(
    (col: number, width: number) => {
      setSheets((prev) =>
        prev.map((s) => (s.id === activeSheetId ? { ...s, colWidths: { ...s.colWidths, [col]: width } } : s)),
      );
    },
    [activeSheetId],
  );

  const subscribeExternals = useCallback(
    (cb: (key: string, active: boolean) => void) => graph.subscribeExternals(cb),
    [graph],
  );

  /**
   * Replace the entire workbook, e.g. when a remote change arrives from another tab or
   * device. The graph is cleared of the current cells and rebuilt from `next` in place
   * (keeping the same graph instance, so the curve subscription stays wired), then
   * recomputed. Undo history is reset, since its entries reference the replaced document.
   */
  const replaceWorkbook = useCallback((next: PersistedWorkbook) => {
    const g = graphRef.current!;
    for (const s of sheetsRef.current) {
      for (const key of Object.keys(s.cells)) g.remove(`${s.id}!${key}`);
    }
    sheetsRef.current = next.sheets;
    const restored = hydrate(next.sheets, g);
    sheetsRef.current = restored;
    setSheets(restored);
    setActiveSheetId(next.sheets.some((s) => s.id === next.activeSheetId) ? next.activeSheetId : restored[0]!.id);
    nextSheetId.current = nextSheetNumber(next.sheets);
    historyRef.current = createHistory<UndoEntry>(HISTORY_CAP);
  }, []);

  return {
    sheets,
    activeSheet,
    activeSheetId,
    setActiveSheetId,
    curves: curvesRef.current,
    subscribeExternals,
    getValue,
    getNode,
    setValue,
    moveRange,
    transact,
    undo,
    redo,
    setColWidth,
    addSheet,
    deleteSheet,
    renameSheet,
    replaceWorkbook,
  };
}
